import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Send, Paperclip, Trash2, FileText, Image, Download, MessageSquare, ZoomIn, ZoomOut, Edit3, Check, AlertTriangle } from 'lucide-react';
import api from '../api/config';

const CATEGORIES = [
  { key: 'info', label: 'Info', color: '#3b82f6', bg: '#eff6ff' },
  { key: 'pendiente', label: 'Pendiente', color: '#f59e0b', bg: '#fffbeb' },
  { key: 'urgente', label: 'Urgente', color: '#ef4444', bg: '#fef2f2' },
  { key: 'apreciacion', label: 'Apreciación', color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'resuelto', label: 'Resuelto', color: '#10b981', bg: '#ecfdf5' },
];

const getCat = (key) => CATEGORIES.find(c => c.key === key) || CATEGORIES[0];

const ObservationsPanel = ({ isOpen, onClose, itemNumber, itemData }) => {
  const [observations, setObservations] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newCategory, setNewCategory] = useState('info');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // observation id
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  const tp = localStorage.getItem('theme-primary-color') || '#c4fffc';
  const ts = localStorage.getItem('theme-secondary-color') || '#000000';

  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const nombre = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Usuario';
        const initials = nombre.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        return { nombre, initials, id: user.id };
      }
      const username = localStorage.getItem('username');
      if (username) {
        return { nombre: username, initials: username.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) };
      }
      return { nombre: 'Usuario', initials: 'U' };
    } catch { return { nombre: 'Usuario', initials: 'U' }; }
  };

  const fetchObservations = async () => {
    if (!itemNumber) return;
    setLoading(true);
    try {
      const res = await api.get(`/observations/${itemNumber}`);
      setObservations(res.data || []);
    } catch (err) {
      console.error('Error fetching observations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && itemNumber) {
      fetchObservations();
      setDeleteConfirm(null);
      setEditingId(null);
    }
  }, [isOpen, itemNumber]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [observations]);

  const handleSend = async () => {
    if (!newComment.trim() && files.length === 0) return;
    setSending(true);
    setUploadProgress(0);
    try {
      const user = getUserInfo();
      const formData = new FormData();
      formData.append('item_number', itemNumber);
      formData.append('content', newComment.trim() || '(archivo adjunto)');
      formData.append('user_name', user.nombre);
      formData.append('user_initials', user.initials);
      formData.append('category', newCategory);
      files.forEach(f => formData.append('files', f));

      await api.post('/observations', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      setNewComment('');
      setFiles([]);
      setNewCategory('info');
      setUploadProgress(0);
      fetchObservations();
    } catch (err) {
      console.error('Error sending observation:', err);
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (obs) => {
    setEditingId(obs.id);
    setEditContent(obs.content);
    setEditCategory(obs.category || 'info');
    setEditAttachments(obs.attachments || []);
  };
  const [editAttachments, setEditAttachments] = useState([]);

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    try {
      await api.patch(`/observations/${editingId}`, {
        content: editContent.trim(),
        category: editCategory
      });
      setEditingId(null);
      setEditAttachments([]);
      fetchObservations();
    } catch (err) {
      console.error('Error editing observation:', err);
    }
  };

  const handleRemoveAttachment = async (attId) => {
    try {
      await api.delete(`/observations/attachment/${attId}`);
      setEditAttachments(prev => prev.filter(a => a.id !== attId));
      // Also refresh to update the observation in the list
      fetchObservations();
    } catch (err) {
      console.error('Error removing attachment:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/observations/${id}`);
      setDeleteConfirm(null);
      fetchObservations();
    } catch (err) {
      console.error('Error deleting observation:', err);
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected].slice(0, 5));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openPreview = (att) => {
    setPreview({ url: att.url, name: att.original_name, mime: att.mime_type, size: att.file_size });
    setPreviewZoom(1);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const isImageMime = (mime) => mime?.startsWith('image/');
  const isPdf = (mime) => mime === 'application/pdf';

  if (!isOpen) return null;

  const clienteLabel = itemData?.cliente || '';
  const descripcionLabel = itemData?.descripcion || '';
  const currentUser = getUserInfo();

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 99998
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '580px', maxWidth: '95vw',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)', border: `3px solid ${ts}`,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: ts, color: tp, padding: '16px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MessageSquare size={22} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', fontFamily: "'Space Grotesk', sans-serif" }}>
                Observaciones - Item #{itemNumber}
              </div>
              {(clienteLabel || descripcionLabel) && (
                <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                  {clienteLabel}{clienteLabel && descripcionLabel ? ' · ' : ''}{descripcionLabel}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: tp,
            cursor: 'pointer', padding: '4px', borderRadius: '6px'
          }}>
            <X size={22} />
          </button>
        </div>

        {/* Observations list */}
        <div ref={listRef} style={{
          flex: 1, overflowY: 'auto', padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: '12px',
          minHeight: '200px', maxHeight: '50vh', background: '#fafafa'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '40px 0', fontSize: '14px' }}>
              Cargando...
            </div>
          ) : observations.length === 0 ? (
            <div style={{
              textAlign: 'center', color: '#999', padding: '40px 0',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
            }}>
              <MessageSquare size={40} strokeWidth={1} color="#ccc" />
              <div style={{ fontSize: '14px' }}>No hay observaciones aún</div>
              <div style={{ fontSize: '12px', color: '#bbb' }}>Sé el primero en agregar una</div>
            </div>
          ) : (
            observations.map(obs => {
              const cat = getCat(obs.category);
              const isEditing = editingId === obs.id;
              const isDeleting = deleteConfirm === obs.id;

              return (
                <div key={obs.id} style={{
                  background: '#fff', borderRadius: '12px', padding: '14px 16px',
                  border: `1px solid ${isDeleting ? '#fca5a5' : '#e5e7eb'}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'border-color 0.2s'
                }}>
                  {/* Delete confirmation inline */}
                  {isDeleting ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '4px 0'
                    }}>
                      <AlertTriangle size={18} color="#ef4444" />
                      <span style={{ flex: 1, fontSize: '13px', color: '#374151' }}>
                        ¿Eliminar esta observación?
                      </span>
                      <button onClick={() => handleDelete(obs.id)} style={{
                        padding: '5px 12px', borderRadius: '6px', border: 'none',
                        background: '#ef4444', color: '#fff', cursor: 'pointer',
                        fontSize: '12px', fontWeight: 600
                      }}>Eliminar</button>
                      <button onClick={() => setDeleteConfirm(null)} style={{
                        padding: '5px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', cursor: 'pointer',
                        fontSize: '12px', fontWeight: 500
                      }}>Cancelar</button>
                    </div>
                  ) : (
                    <>
                      {/* Author row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%',
                          background: ts, color: tp, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: '11px', fontWeight: 700, flexShrink: 0,
                          border: `2px solid ${tp}`
                        }}>
                          {obs.user_initials || obs.user_name?.charAt(0) || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>
                              {obs.user_name}
                            </span>
                            <span style={{
                              fontSize: '10px', fontWeight: 600, padding: '1px 7px',
                              borderRadius: '10px', color: cat.color, background: cat.bg,
                              border: `1px solid ${cat.color}30`
                            }}>{cat.label}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {formatDate(obs.created_at)}
                            {obs.updated_at !== obs.created_at && ' (editado)'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button onClick={() => handleEdit(obs)} title="Editar" style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: '#d1d5db', padding: '4px', borderRadius: '4px'
                          }}
                            onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
                            onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                          ><Edit3 size={13} /></button>
                          <button onClick={() => setDeleteConfirm(obs.id)} title="Eliminar" style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: '#d1d5db', padding: '4px', borderRadius: '4px'
                          }}
                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                          ><Trash2 size={13} /></button>
                        </div>
                      </div>

                      {/* Content or edit mode */}
                      {isEditing ? (
                        <div style={{ marginLeft: '42px' }}>
                          <textarea
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            style={{
                              width: '100%', minHeight: '60px', border: `2px solid ${ts}`,
                              borderRadius: '8px', padding: '8px 10px', fontSize: '13px',
                              fontFamily: "'Space Grotesk', sans-serif", resize: 'vertical',
                              outline: 'none', boxSizing: 'border-box'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {CATEGORIES.map(c => (
                              <button key={c.key} onClick={() => setEditCategory(c.key)} style={{
                                padding: '2px 8px', borderRadius: '10px', border: `1.5px solid ${c.color}`,
                                background: editCategory === c.key ? c.bg : '#fff',
                                color: c.color, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                opacity: editCategory === c.key ? 1 : 0.5
                              }}>{c.label}</button>
                            ))}
                          </div>
                          {/* Editable attachments */}
                          {editAttachments.length > 0 && (
                            <div style={{
                              display: 'flex', gap: '8px', overflowX: 'auto',
                              marginTop: '8px', paddingBottom: '4px'
                            }}>
                              {editAttachments.map(att => (
                                <div key={att.id} style={{
                                  flexShrink: 0, position: 'relative', borderRadius: '8px',
                                  overflow: 'hidden', border: '1px solid #e5e7eb'
                                }}>
                                  {isImageMime(att.mime_type) ? (
                                    <div style={{ width: '90px', height: '65px' }}>
                                      <img src={att.url} alt={att.original_name} style={{
                                        width: '100%', height: '100%', objectFit: 'cover', display: 'block'
                                      }} />
                                    </div>
                                  ) : (
                                    <div style={{
                                      padding: '8px 10px', background: '#f9fafb', fontSize: '10px',
                                      display: 'flex', alignItems: 'center', gap: '4px', maxWidth: '120px'
                                    }}>
                                      <FileText size={12} color={ts} />
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {att.original_name}
                                      </span>
                                    </div>
                                  )}
                                  <button onClick={() => handleRemoveAttachment(att.id)} style={{
                                    position: 'absolute', top: '2px', right: '2px',
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    background: '#ef4444', border: '1.5px solid #fff', color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', padding: 0
                                  }}><X size={10} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setEditingId(null); setEditAttachments([]); }} style={{
                              padding: '5px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
                              background: '#fff', fontSize: '12px', cursor: 'pointer'
                            }}>Cancelar</button>
                            <button onClick={handleSaveEdit} style={{
                              padding: '5px 12px', borderRadius: '6px', border: 'none',
                              background: ts, color: tp, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}><Check size={12} /> Guardar</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '13.5px', color: '#374151', lineHeight: '1.6',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginLeft: '42px'
                        }}>
                          {obs.content}
                        </div>
                      )}

                      {/* Attachments - horizontal scroll */}
                      {obs.attachments && obs.attachments.length > 0 && !isEditing && (
                        <div style={{
                          marginTop: '10px', marginLeft: '42px',
                          display: 'flex', gap: '8px', overflowX: 'auto',
                          paddingBottom: '4px'
                        }}>
                          {obs.attachments.map(att => (
                            <div key={att.id} onClick={() => openPreview(att)} style={{
                              flexShrink: 0, cursor: 'pointer', borderRadius: '10px',
                              overflow: 'hidden', border: '1.5px solid #e5e7eb',
                              transition: 'border-color 0.2s, box-shadow 0.2s',
                              width: isImageMime(att.mime_type) ? '150px' : 'auto'
                            }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = ts; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                              {isImageMime(att.mime_type) ? (
                                <div style={{ position: 'relative', width: '150px', height: '110px' }}>
                                  <img src={att.url} alt={att.original_name} style={{
                                    width: '100%', height: '100%', objectFit: 'cover', display: 'block'
                                  }} />
                                  <div style={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                                    padding: '4px 6px', fontSize: '10px', color: '#fff',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                  }}>{att.original_name}</div>
                                </div>
                              ) : (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  padding: '12px 14px', background: '#f9fafb', fontSize: '12px',
                                  color: '#374151', whiteSpace: 'nowrap', height: '110px',
                                  flexDirection: 'column', justifyContent: 'center', width: '110px'
                                }}>
                                  <FileText size={28} color={ts} />
                                  <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '10px', textAlign: 'center' }}>
                                    {att.original_name}
                                  </span>
                                  <span style={{ fontSize: '9px', color: '#9ca3af' }}>{formatSize(att.file_size)}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Upload progress bar */}
        {sending && uploadProgress > 0 && (
          <div style={{ padding: '0 20px' }}>
            <div style={{
              height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden'
            }}>
              <div style={{
                height: '100%', background: ts, borderRadius: '2px',
                width: `${uploadProgress}%`, transition: 'width 0.2s'
              }} />
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', padding: '4px 0' }}>
              Subiendo... {uploadProgress}%
            </div>
          </div>
        )}

        {/* Input area */}
        <div style={{
          borderTop: '1px solid #e5e7eb', padding: '12px 20px', background: '#fff'
        }}>
          {/* Category selector */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => (
              <button key={c.key} onClick={() => setNewCategory(c.key)} style={{
                padding: '2px 10px', borderRadius: '12px',
                border: `1.5px solid ${c.color}`,
                background: newCategory === c.key ? c.bg : '#fff',
                color: c.color, fontSize: '11px', fontWeight: 600,
                cursor: 'pointer', opacity: newCategory === c.key ? 1 : 0.45,
                transition: 'all 0.15s'
              }}>{c.label}</button>
            ))}
          </div>

          {/* File preview chips - horizontal scroll */}
          {files.length > 0 && (
            <div style={{
              display: 'flex', gap: '6px', marginBottom: '8px',
              overflowX: 'auto', paddingBottom: '2px'
            }}>
              {files.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '4px 8px', borderRadius: '6px', flexShrink: 0,
                  background: `${tp}30`, border: `1px solid ${ts}30`,
                  fontSize: '11px', color: '#374151'
                }}>
                  <Paperclip size={10} />
                  <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <button onClick={() => removeFile(i)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', padding: '0 2px', lineHeight: 1
                  }}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Adjuntar archivos (máx. 5)"
              disabled={sending}
              style={{
                background: 'transparent', border: '2px solid #e5e7eb',
                borderRadius: '10px', padding: '8px', cursor: 'pointer',
                color: '#6b7280', transition: 'all 0.2s', flexShrink: 0
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = ts; e.currentTarget.style.color = ts; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <Paperclip size={18} />
            </button>
            <input ref={fileInputRef} type="file" multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              style={{ display: 'none' }} onChange={handleFileSelect} />

            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Escribe una observación..."
              rows={1}
              disabled={sending}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              onInput={e => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
              }}
              style={{
                flex: 1, resize: 'none', border: '2px solid #e5e7eb',
                borderRadius: '10px', padding: '10px 14px', fontSize: '13px',
                fontFamily: "'Space Grotesk', sans-serif", outline: 'none',
                transition: 'border-color 0.2s', minHeight: '40px', maxHeight: '100px',
                lineHeight: '1.4', opacity: sending ? 0.5 : 1
              }}
              onFocus={e => e.target.style.borderColor = ts}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />

            <button
              onClick={handleSend}
              disabled={sending || (!newComment.trim() && files.length === 0)}
              style={{
                background: ts, color: tp, border: 'none',
                borderRadius: '10px', padding: '10px', cursor: 'pointer',
                transition: 'all 0.2s', flexShrink: 0,
                opacity: sending || (!newComment.trim() && files.length === 0) ? 0.4 : 1
              }}
            >
              {sending ? (
                <div style={{
                  width: '18px', height: '18px', border: `2px solid ${tp}`,
                  borderTopColor: 'transparent', borderRadius: '50%',
                  animation: 'obs-spin 0.6s linear infinite'
                }} />
              ) : <Send size={18} />}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '5px', textAlign: 'center' }}>
            Enter para enviar · Shift+Enter nueva línea · Máx. 5 archivos
          </div>
        </div>
      </div>

      {/* Spinner keyframe (injected once) */}
      <style>{`@keyframes obs-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Preview modal */}
      {preview && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 999999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setPreview(null)}
        >
          <div onClick={e => e.stopPropagation()} style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            padding: '14px 20px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', background: 'rgba(0,0,0,0.5)', zIndex: 2
          }}>
            <div style={{ color: '#fff', fontSize: '14px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>
              {preview.name}
              <span style={{ color: '#999', fontWeight: 400, marginLeft: '10px', fontSize: '12px' }}>
                {formatSize(preview.size)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isImageMime(preview.mime) && (
                <>
                  <button onClick={() => setPreviewZoom(z => Math.max(0.25, z - 0.25))} style={{
                    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px',
                    padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex'
                  }}><ZoomOut size={18} /></button>
                  <span style={{ color: '#ccc', fontSize: '12px', minWidth: '40px', textAlign: 'center' }}>
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button onClick={() => setPreviewZoom(z => Math.min(3, z + 0.25))} style={{
                    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px',
                    padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex'
                  }}><ZoomIn size={18} /></button>
                </>
              )}
              <a href={preview.url} download={preview.name} onClick={e => e.stopPropagation()} style={{
                background: tp, color: ts, border: 'none', borderRadius: '8px',
                padding: '6px 14px', textDecoration: 'none', fontSize: '13px', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'Space Grotesk', sans-serif"
              }}><Download size={16} /> Descargar</a>
              <button onClick={() => setPreview(null)} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px',
                padding: '6px', cursor: 'pointer', color: '#fff', display: 'flex'
              }}><X size={20} /></button>
            </div>
          </div>

          <div onClick={e => e.stopPropagation()} style={{
            maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {isImageMime(preview.mime) ? (
              <img src={preview.url} alt={preview.name} style={{
                maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain',
                borderRadius: '8px', transform: `scale(${previewZoom})`, transition: 'transform 0.2s'
              }} />
            ) : isPdf(preview.mime) ? (
              <iframe src={preview.url} title={preview.name} style={{
                width: '80vw', height: '80vh', border: 'none', borderRadius: '8px', background: '#fff'
              }} />
            ) : (
              <div style={{
                background: '#1a1a2e', borderRadius: '16px', padding: '40px', textAlign: 'center', color: '#fff'
              }}>
                <FileText size={64} color={tp} style={{ marginBottom: '16px' }} />
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>{preview.name}</div>
                <div style={{ fontSize: '13px', color: '#999', marginBottom: '20px' }}>{formatSize(preview.size)}</div>
                <a href={preview.url} download={preview.name} onClick={e => e.stopPropagation()} style={{
                  background: tp, color: ts, padding: '10px 24px', borderRadius: '8px',
                  textDecoration: 'none', fontWeight: 600, fontSize: '14px',
                  display: 'inline-flex', alignItems: 'center', gap: '8px'
                }}><Download size={18} /> Descargar archivo</a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ObservationsPanel;
